import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Alert } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import logo from '../../assets/logo.png';
import '../../fonts.css';
import backgroundImage from '../../assets/background.jpg';
import { API_BASE_URL } from '../../config/api';;

const Login = () => {
  const emailRef = useRef();
  const passwordRef = useRef();
  const { login, signInWithGoogle, signInWithFacebook, logout } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);
      await login(emailRef.current.value, passwordRef.current.value);
      navigate('/');
    } catch (error) {
      if (error.message === 'Firebase: Error (auth/invalid-credential).') {
        setError('User not found!');
      } else {
        setError('Failed to sign in: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    try {
      setError('');
      setLoading(true);
      
      // Sign in with Google via Firebase
      const userCredential = await signInWithGoogle();
      
      // Get the token for backend verification
      const token = await userCredential.user.getIdToken();

      console.log("TOKEN:",token);
      
      // Verify with backend, indicating this is from login page
      const response = await fetch(`${API_BASE_URL}/auth/verify-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idToken: token,
          sourcePage: 'login'  // Indicate this is from login page
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Handle specific errors
        if (data.error === 'account_not_found') {
          // Clean up by signing out the user
          await logout();
          throw new Error('Account not found. Please sign up first to create an account.');
        }
        throw new Error(data.error || 'Authentication failed');
      }
      
      // Success - navigate to dashboard
      navigate('/');
    } catch (error) {
      console.error('Google sign-in error:', error);
      setError(error.message || 'Failed to sign in with Google');
      
      // If an account was created but it shouldn't have been, clean up
      try {
        await logout();
      } catch (logoutError) {
        console.error('Logout error:', logoutError);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleFacebookSignIn() {
    try {
      setError('');
      setLoading(true);
      
      // Sign in with Facebook via Firebase
      const userCredential = await signInWithFacebook();
      
      // Get the token for backend verification
      const token = await userCredential.user.getIdToken();
      
      // Verify with backend, indicating this is from login page
      const response = await fetch(`${API_BASE_URL}/auth/verify-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idToken: token,
          sourcePage: 'login'  // Indicate this is from login page
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Handle specific errors
        if (data.error === 'account_not_found') {
          // Clean up by signing out the user
          await logout();
          throw new Error('Account not found. Please sign up first to create an account.');
        }
        throw new Error(data.error || 'Authentication failed');
      }
      
      // Success - navigate to dashboard
      navigate('/');
    } catch (error) {
      console.error('Facebook sign-in error:', error);
      setError(error.message || 'Failed to sign in with Facebook');
      
      // If an account was created but it shouldn't have been, clean up
      try {
        await logout();
      } catch (logoutError) {
        console.error('Logout error:', logoutError);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      {/* Background image with opacity */}
      <div 
        className="absolute inset-0 z-0" 
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.2, // 20% opacity
        }}
      ></div>
      <div className="relative z-10 max-w-md w-full space-y-8 bg-white bg-opacity-10 p-8 rounded-lg shadow-lg">
        <div>
          <div className="flex justify-center mb-4">
            <img 
              src={logo}
              alt="Flokraft Logo" 
              className="h-24 w-auto"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 metalord-font">
            AI Match Analysis
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to access your account
          </p>
        </div>
        
        {error && <Alert variant="destructive">{error}</Alert>}
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                ref={emailRef}
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                ref={passwordRef}
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Password"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Link to="/forgot-password" className="font-medium text-blue-600 hover:text-blue-500">
                Forgot your password?
              </Link>
            </div>
          </div>

          <div>
            <Button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </div>
          
          <div className="flex items-center justify-center">
            <span className="text-gray-500 text-sm">or continue with</span>
          </div>
          
          <div className="grid gap-3">
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  d="M12.545 10.239v3.821h5.445c-0.712 2.315-2.647 3.972-5.445 3.972-3.332 0-6.033-2.701-6.033-6.032s2.701-6.032 6.033-6.032c1.498 0 2.866 0.549 3.921 1.453l2.814-2.814c-1.79-1.677-4.184-2.702-6.735-2.702-5.514 0-10 4.486-10 10s4.486 10 10 10c5.514 0 10-4.486 10-10 0-0.837-0.12-1.64-0.329-2.411h-9.671z"
                  fill="#4285F4"
                />
              </svg>
              <span className="ml-2">Google</span>
            </button>
            
            
          </div>
        </form>
        
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Need an account?{' '}
            <Link to="/signup" className="font-medium text-blue-600 hover:text-blue-500">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;